import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TripsSection from '../components/TripsSection';
import SEO from '../components/SEO';

type Creator = {
  id: string;
  full_name?: string | null;
  brand_name?: string | null;
  profile_photo_url?: string | null;
  country?: string | null;
  city?: string | null;
  primary_niche?: string | null;
  total_followers?: number | null;
  avg_engagement_rate?: number | null;
};

type CreatorsResponse = {
  success: boolean;
  creators: Creator[];
  countries: string[];
  total: number;
  hasMore?: boolean;
};

export default function RecommendedPage() {
  const [topInfluencers, setTopInfluencers] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadTopInfluencers = async () => {
      try {
        const response = await fetch('/api/creators?limit=5&offset=0');
        if (response.ok) {
          const data: CreatorsResponse = await response.json();
          setTopInfluencers(data.creators ?? []);
        }
      } catch (error) {
        console.error('Failed to load top influencers:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTopInfluencers();
  }, []);

  return (
    <div className="flex-1 bg-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Top Influencers Section */}
        <div className="mb-16">
          <h2 className="text-2xl font-semibold mb-8">Top Travel Influencers</h2>
          
          {loading ? (
            <div className="flex gap-4 overflow-x-auto pb-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="shrink-0 w-56 h-64 bg-gray-200 animate-pulse rounded" />
              ))}
            </div>
          ) : (
            <>
              <div className="flex gap-4 overflow-x-auto pb-4">
                {topInfluencers.map((creator) => {
                  const name = creator.full_name || creator.brand_name || 'Unknown';
                  return (
                    <div 
                      key={creator.id}
                      onClick={() => navigate(`/creator/${creator.id}`)}
                      className="shrink-0 w-56 border border-gray-200 hover:border-black transition-colors group cursor-pointer"
                    >
                      {creator.profile_photo_url ? (
                        <div className="h-56 overflow-hidden bg-gray-100">
                          <img 
                            src={creator.profile_photo_url} 
                            alt={name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                        </div>
                      ) : (
                        <div className="h-56 bg-gray-200 flex items-center justify-center">
                          <span className="text-4xl font-bold text-gray-400">{name.charAt(0)}</span>
                        </div>
                      )}
                      <div className="p-4">
                        <h3 className="font-semibold text-sm truncate">{name}</h3>
                        <p className="text-xs text-gray-600">{creator.primary_niche || 'Travel'}</p>
                        {creator.country && (
                          <p className="text-xs text-gray-500 mt-1">{creator.country}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <Link 
                to="/recommended/top-influencers"
                className="inline-block mt-6 bg-black text-waybook-green px-6 py-3 font-semibold hover:bg-gray-900 transition-colors rounded-lg"
              >
                View All Top Influencers
              </Link>
            </>
          )}
        </div>

        {/* Recommended Trips Section */}
        <div className="mb-16">
          <h2 className="text-2xl font-semibold mb-8">Recommended Trips</h2>
          <TripsSection limit={5} showViewAllLink={true} title="" />
        </div>
      </div>
    </div>
  );
}
